import { Button, CircularProgress } from "@mui/material";
import { UploadFile as UploadFileIcon } from "@mui/icons-material";
import { useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

const ACCEPT = "application/pdf,image/*,.pdf,.heic,.heif";

/**
 * Client request (5): manually attach an invoice document to a transaction
 * that is missing one (or has the wrong one). Picks a PDF/image, uploads it to
 * Convex storage and calls `invoices.attachManualUpload`, which replaces any
 * existing (wrong) match with a human-confirmed match to the new invoice.
 */
export function UploadInvoiceButton({
  transactionId,
  actorEmail,
  hasInvoice = false,
  onResult,
  size = "small",
  variant = "outlined",
  label,
}: {
  transactionId: string;
  actorEmail: string;
  /** When the txn already has a matched invoice, the button reads "Replace". */
  hasInvoice?: boolean;
  onResult: (message: string, severity: "success" | "error") => void;
  size?: "small" | "medium";
  variant?: "outlined" | "contained" | "text";
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const generateUploadUrl = useMutation(api.storage.generateUploadUrl);
  const attachManualUpload = useMutation(api.invoices.attachManualUpload);

  const handleFile = async (file: File) => {
    if (!actorEmail) {
      onResult("Could not identify the signed-in user — please sign in again.", "error");
      return;
    }
    setBusy(true);
    try {
      const url = await generateUploadUrl();
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
      const { storageId } = (await resp.json()) as { storageId: string };
      const result = await attachManualUpload({
        transactionId: transactionId as Id<"transactions">,
        storageId: storageId as Id<"_storage">,
        fileName: file.name,
        actorEmail,
      });
      if (!result.ok) {
        onResult(result.message, "error");
        return;
      }
      onResult(
        result.replacedMatches > 0
          ? `Invoice "${file.name}" attached — the previous invoice was unlinked.`
          : `Invoice "${file.name}" attached and matched.`,
        "success",
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Convex wraps server errors as "[CONVEX M(...)] ... Uncaught Error: <msg>"
      const clean = msg.split("Uncaught Error:").pop()?.split("\n")[0]?.trim() || msg;
      onResult(`Invoice upload failed: ${clean}`, "error");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />
      <Button
        size={size}
        variant={variant}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon fontSize="small" />}
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.click();
        }}
      >
        {busy ? "Uploading..." : label ?? (hasInvoice ? "Replace invoice" : "Upload invoice")}
      </Button>
    </>
  );
}
